// ============================================================================
// LOAN MAPPING SERVICE
// ============================================================================

import { get, put } from '../client/axiosClient';
import { loanMappingEndpoints } from '../endpoints/loanMapping.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  ID,
} from '../types';
import { transformPaginatedResponse } from '../types/common.types';
import type {
  LoanMapping,
  LoanMappingDetail,
  LoanMappingFilterParams,
  LoanMappingReportDetail,
  LoanMappingReportFilterParams,
} from '../types/loanMapping.types';

class LoanMappingService {
  async getAll(params?: LoanMappingFilterParams): Promise<PaginatedResponse<LoanMapping>> {
    const endpoint = params
      ? buildEndpointWithQuery(loanMappingEndpoints.list(), params)
      : loanMappingEndpoints.list();
    const response = await get(endpoint);
    return transformPaginatedResponse<LoanMapping>(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<LoanMappingDetail>> {
    const response = await get<ApiResponse<LoanMappingDetail>>(loanMappingEndpoints.getById(id));
    return response.data;
  }

  // Bulk fetch for the Loan Mapping Report Detail export — one call returns all
  // matching mappings with their GLs nested. Replaces the previous N+1 getById fan-out.
  async getReportDetail(params?: LoanMappingReportFilterParams): Promise<ApiResponse<LoanMappingReportDetail[]>> {
    const endpoint = params
      ? buildEndpointWithQuery(loanMappingEndpoints.reportDetail(), params)
      : loanMappingEndpoints.reportDetail();
    const response = await get<ApiResponse<LoanMappingReportDetail[]>>(endpoint);
    return response.data;
  }

  // Server-side Excel export: the backend builds the (filtered) .xlfile and streams
  // it as a blob, so the grid never holds the whole dataset. Uses a longer timeout
  // because the server aggregates the full result set for the download.
  async downloadReport(
    params: LoanMappingReportFilterParams & { format: 'summary' | 'detail' },
    appliedFilters?: string[]
  ): Promise<void> {
    let endpoint = buildEndpointWithQuery(loanMappingEndpoints.reportExport(), params as unknown as Record<string, unknown>);
    if (appliedFilters && appliedFilters.length > 0) {
      const extra = appliedFilters.map((f) => `appliedFilters=${encodeURIComponent(f)}`).join('&');
      endpoint += (endpoint.includes('?') ? '&' : '?') + extra;
    }

    const response = await get(endpoint, { responseType: 'blob', timeout: 120000 });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const disposition = (response.headers?.['content-disposition'] as string) || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const fileName = match
      ? decodeURIComponent(match[1])
      : `Loan-Mapping-Report-${params.format}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getByCustomerId(customerId: string): Promise<ApiResponse<LoanMapping[]>> {
    const response = await get<ApiResponse<LoanMapping[]>>(loanMappingEndpoints.getByCustomerId(customerId));
    return response.data;
  }

  async searchByAccount(accountNumber?: string, glNumber?: string): Promise<LoanMappingDetail | null> {
    const response = await get<ApiResponse<LoanMappingDetail | null>>(
      loanMappingEndpoints.searchByAccount(accountNumber, glNumber)
    );
    return response.data.data ?? null;
  }

  async amendAccountNumber(id: ID, payload: { newNumber: string; reason: string }): Promise<ApiResponse<LoanMappingDetail>> {
    const response = await put<ApiResponse<LoanMappingDetail>>(loanMappingEndpoints.amendAccount(id), payload);
    return response.data;
  }

  async amendGlNumber(id: ID, glId: ID, payload: { newNumber: string; reason: string }): Promise<ApiResponse<LoanMappingDetail>> {
    const response = await put<ApiResponse<LoanMappingDetail>>(loanMappingEndpoints.amendGl(id, glId), payload);
    return response.data;
  }
}

export const loanMappingService = new LoanMappingService();
export default loanMappingService;
